import { Pipe, PipeTransform } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { TranslateService } from '../../services/translate/translate.service';
import { ContentService } from '../../services/content/content.service';
import * as _ from 'lodash';

@Pipe({ name: 'contentEvent' })
export class EventPipe implements PipeTransform {

  today = null;
  future = null;
  dateFormat: any = { day: '2-digit', month: '2-digit' };
  customIds = [];

  constructor(private contentService: ContentService, private translate: TranslateService) {
    const day = EventPipe.getNulledDate();
    this.today = +day.getTime() / 1000;
    this.future = +day.setMonth(day.getMonth() + 6) / 1000;
    /* future: we do not display events more than 6 months in the future */
  }

  private static getDateUnix(dateString, isEndDate = false) {
    const res = EventPipe.getDate(dateString, isEndDate);
    return !!res ? res.getTime() / 1000 : null;
  }

  private static getDate(dateString, isEndDate = false) {
    if (!dateString) return null;
    let res = /\d?\d\.\d?\d\.(\d\d|\d\d\d\d)/.test(dateString) ? new Date(dateString.split('.').reverse().join('/')) : new Date(dateString);
    if (isEndDate) {
      res.setHours(23, 59, 59);
    }
    return res;
  }


  private static getNulledDate() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private static format(date, format = 'DD.MM.YYYY') {
    const groups = format.split('.');
    return groups.map(group => {
      const value = group.startsWith('D') ? date.getDate() :
        group.startsWith('M') ? date.getMonth() + 1 :
          date.getFullYear();

      if (group.startsWith('D') || group.startsWith('M')) {
        return String(value).padStart(group.length, '0');
      } else {
        return group.length === 4 ? value : String(value).slice(-2);
      }
    }).join('.');
  }

  transform(entry: Models.ContentEntry): Models.EventEntry | Models.EventEntry[] {
    if (!entry) {
      return null;
    }
    /* event master */
    if ((entry?.related?.entries?.length || 0) > 0) {
      let entries: Models.EventEntry[] = (entry?.related?.entries || []).map(item => {
        const contentEntry = this.contentService.getEntry(item.id);
        if (contentEntry && this.checkEventDate(contentEntry)) {
          const event = this.transform(contentEntry) as Models.EventEntry;
          if (!!event && !this.customIds.includes(contentEntry.importCustomId)) {
            if (contentEntry.importCustomId && !this.customIds.includes(contentEntry.importCustomId)) {
              this.customIds.push(contentEntry.importCustomId);
            }
            return event;
          }
        }
      }).filter(Boolean);

      entries = _.orderBy(entries, 'orderTimestamp');
      return entries.length === 0 ? null : entries;
    }

    /* event detail */
    if (!entry.detail) {
      return null;
    }

    const event = new Models.EventEntry();

    let media = this.contentService.getMediaEntriesForEntry(entry.getId());
    const documents = this.contentService.getDocumentEntriesForEntry(entry.getId());

    media = _.orderBy(media, ['type'], ['asc']);

    const eventContent: any = {
      sourceId: entry._id, title: entry.title, subtitle: entry.detail.subtitle, teaser: entry.detail.introduction,
      description: entry.detail.description, additionalInformation: entry.detail.openingHours, website: entry.detail.website,
      dateStrings: this.removePastDates(entry.detail.dateStrings), dateRanges: this.removePastItems(entry.detail.dateRanges),
      shortDateStrings: null, media, documents, orderTimestamp: entry.detail.orderTimestamp
    };

    if ((!eventContent.dateRanges || !eventContent.dateRanges.length) && entry.detail && ((entry.detail.multipleDates && entry.detail.multipleDates.length) || (entry.detail.dates && entry.detail.dates.length))) {
      const ranges = [];
      for (let date of entry.detail.multipleDates || (entry.detail.dates.length ? entry.detail.dates : [entry.detail.dates])) {
        const start = new Date(date.startDate);
        start.setHours(0, 0);
        const end = date.endDate ? new Date(date.endDate) : new Date(start.getTime());
        end.setHours(23, 59);
        ranges.push({ start: start.getTime() / 1000, end: end.getTime() / 1000 });
      }
      eventContent.dateRanges = ranges;
    }

    this.enhanceEventDetail(eventContent, entry);

    if (entry.detail.dates) {
      eventContent.shortDateStrings = this.prettifyDate(entry);
    }
    if (eventContent && eventContent.dateRanges && entry && entry.detail && entry.detail.dates
      && eventContent.dateRanges.length === entry.detail.dates.length) {
      for (let i = 0; i < eventContent.dateRanges.length; i++) {
        eventContent.dateRanges[i]['days'] = this.getDaysArray(entry.detail.dates[i]);
      }
    }
    let dateStringsObj: any = eventContent.dateStringsObj;
    event.populateFromJSON(eventContent);
    event['dateStringsObj'] = dateStringsObj;
    return event;

  }

  private enhanceEventDetail(eventContent, entry) {
    const { detail: data } = entry;
    const { location, contact, bookable, raw } = data;
    eventContent.repetition = entry?.detail?.repetition;
    if (location?.coordinates) {
      const { latitude, longitude } = location.coordinates;
      eventContent.latitude = latitude;
      eventContent.longitude = longitude;
    }
    if (location) {
      eventContent.street = location.address?.trim();
      eventContent.city = location.city?.trim();
      eventContent.zip = location.zip?.trim();
    }

    contact?.[0]?.variants?.forEach(variant => {
      if (variant.type === 'phone') eventContent.phone = variant.value;
      if (variant.type === 'fax') eventContent.fax = variant.value;
      if (variant.type === 'email') eventContent.email = variant.value;
    });

    let parsedRaw;
    try {
      parsedRaw = raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('could not parse raw for entry', entry.title, err);
    }

    if (bookable) {
      Object.assign(eventContent, {
        bookable, bookingUrl: data.bookingUrl, bookingText: data?.bookingTextScreen || data?.bookingText, products: data.products,
        productsType: data.productsType
      });
    } else if (parsedRaw?.bookable && parsedRaw.bookingUrl) {
      Object.assign(eventContent, { bookable: parsedRaw.bookable, bookingUrl: parsedRaw.bookingUrl, bookingText: parsedRaw.bookingText });
    }

    if (entry.detail.dates && (
      (_.isArray(data.dates) && data.dates[0]?.startDate && data.dates[0]?.daily !== undefined) ||
      (!_.isArray(data.dates) && data.dates.startDate && data.dates.daily !== undefined)
    )) {
      const { ibexfusionDates } = this.getIbexfusionDate(entry);
      eventContent.dateStrings = ibexfusionDates;
      eventContent.shortDateStrings = this.getIbexfusionDate(entry, true).ibexfusionDates;
      eventContent.dateStringsObj = this.getIbexfusionDate(entry, false, true);
    }

    if (!eventContent.dateRanges && entry.detail.validity?.start && entry.detail.validity?.end) {
      eventContent.dateRanges = [entry.detail.validity];
    }
  }

  private checkEventDate(event): boolean {
    if (event?.detail?.ibexPublishing && this.contentService.isValid(event)) {
      return true;
    } else if (event.detail && event.detail.validity && !event.detail.multipleDates) {
      return this.checkValidDate(event.detail.validity.start, event.detail.validity.end);
    } else if (event.detail && event.detail.dates) {
      if (!_.isArray(event.detail.dates)) {
        event.detail.dates = [event.detail.dates];
      } else if (event.detail.dates.length <= 0) {
        return true;
      }

      const eventDates = event.detail.multipleDates ? event.detail.multipleDates : event.detail.dates;

      let validDate = false;
      for (const date of eventDates) {
        if (date.daily === undefined) { /* what is daily for - why do we exclude it here ? */
          continue;
        }
        const startDate = EventPipe.getDateUnix(date.startDate);
        const endDate = EventPipe.getDateUnix(date.endDate, true);
        validDate = (!startDate && !endDate) || this.checkValidDate(startDate, endDate);

        if (!event?.orderTimestamp && validDate && startDate) {
          event.orderTimestamp = startDate;
        }
      }
      return validDate;
    }
    return false;
  }

  private checkValidDate(startDate, endDate) {
    if ((endDate && this.today > endDate) || (!endDate && this.today > startDate)) {
      return false;
    }
    return this.future >= startDate;
  }

  private prettifyDate(event: Models.ContentEntry) {
    if (!_.isArray(event.detail.dates)) {
      event.detail.dates = [event.detail.dates];
    }

    const eventDates = event.detail.multipleDates || event.detail.dates;

    return eventDates.filter(date => date.startDate || date.endDate).map(date => {
        const formatDate = (dateStr) => dateStr.indexOf('T') > -1 ? new Date(dateStr).toLocaleDateString('de-CH', this.dateFormat) : dateStr;

        const startDate = formatDate(date.startDate);
        const endDate = date.endDate ? formatDate(date.endDate) : null;

        let dateString = `${startDate.substring(0, 5)}${endDate && endDate !== startDate ? ` - ${endDate.substring(0, 5)}` : ''}`;
        if (date.days) dateString += ` ${this.getDayString(date.days)}`;

        const timeString = date.startTime ? `${date.startTime}${date.endTime ? ` - ${date.endTime}` : ''}` : '';
        if (timeString) dateString += `, ${timeString}`;

        return dateString;
      });
  }


  private getDayString(days) {
    const weekdayArray = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const dayArray = weekdayArray.map(day => days.includes(day));

    if (dayArray.every(day => day) || !dayArray.some(Boolean)) return null;

    const rangePattern = (start, end) => `|${start}| - |${end}|`;

    let dayString = '';
    let i = 0;

    while (i < dayArray.length) {
      if (dayArray[i]) {
        let j = i;
        while (j < dayArray.length && dayArray[j]) j++;  // Find consecutive true values
        const range = j - i > 1 ? rangePattern(weekdayArray[i], weekdayArray[j - 1]) : `|${weekdayArray[i]}|`;
        dayString += (dayString ? ',' : '') + range;
        i = j;
      } else {
        i++;
      }
    }

    return dayString.replace(/\|(\d?\d)\|/g, (_, day) => this.getTranslatedWeekday(day, true)) || null;
  }

  private getIbexfusionDate(entry: Models.ContentEntry, short = false, dateObj = false): { ibexfusionDate: string, ibexfusionDates: string[] } {
    let multipleDates = entry.detail.multipleDates || (entry.detail.dates ? (_.isArray(entry.detail.dates) ? entry.detail.dates : [entry.detail.dates]) : null);

    if (!multipleDates) return null;

    const now = new Date().getTime() / 1000;
    let closestIndex = 0;
    let lowestDifference = Infinity;

      if (dateObj) {
          const dateStringsObj = multipleDates.reduce((acc, date) => {
              const obj = this.getIbexfusionDateObj(date);
              if (obj) {
                  Object.assign(acc, obj);
              }
              return acc;
          }, {});
          return dateStringsObj;
      }

    const multipleIbexfusionDates = multipleDates.map((date, i) => {
      const startDateUnix = EventPipe.getDateUnix(date.startDate);
      const endDateUnix = EventPipe.getDateUnix(date.endDate, true);

      if (endDateUnix && endDateUnix < now && multipleDates.length > 1) {
        return null;
      }

      if (startDateUnix && startDateUnix - now < lowestDifference) {
        lowestDifference = startDateUnix - now;
        closestIndex = i;
      }
      return this.getIbexfusionDateString(date, short);
    }).filter(Boolean);

    return { ibexfusionDates: multipleIbexfusionDates, ibexfusionDate: multipleIbexfusionDates[closestIndex] };
  }

    private getIbexfusionDateObj(date) {
        const {startDate, startTime} = date;
        const start = EventPipe.getDate(startDate);

        return {
            [start.getTime()]: startTime || ''
        };
    }


  private getIbexfusionDateString(date, short = false) {
    const { startDate, endDate, startTime, endTime, daily, days } = date;
    const from = this.translate?.get('general.from') || '';
    const to = this.translate?.get('general.to') || '';
    const start = EventPipe.getDate(startDate);
    const end = EventPipe.getDate(endDate, true);

    if (!start && !end) return null;

    const dateFormat = ((end && start.getFullYear() === end.getFullYear()) || !end && start.getFullYear() === new Date().getFullYear()) ? 'DD.MM.' : 'DD.MM.YYYY';
    const dayString = days ? this.getDayString(days) : null;

    if (daily) {
      let res = EventPipe.format(start, dateFormat) + (end && EventPipe.format(start, 'D.M.YYYY') !== EventPipe.format(end, 'D.M.YYYY') ? ' - ' + EventPipe.format(end, dateFormat) : '');
      if (dayString) res += ' / ' + dayString;
      if (!short && startTime) {
        res += ` ${startTime}${endTime ? ' - ' + endTime : ''}`;
      }
      return res;
    }

    // Handle non-daily events
    const timeString = startTime ? (endTime ? `${startTime} - ${endTime}` : `${from} ${startTime}`) : '';
    const dateRangeString = end ? `${EventPipe.format(start, dateFormat)} ${timeString} ${to} ${EventPipe.format(end, dateFormat)} ${endTime || ''}` : startTime ? `${EventPipe.format(start, dateFormat)} ${from} ${startTime}` : `${EventPipe.format(start, dateFormat)}`;

    return dateRangeString.trim();
  }


  private getTranslatedWeekday(num, short = false) {
    return this.translate.get(`general.weekday.${short ? 'short.' : ''}${num % 7}`);
  }

  private getDaysArray(range: any): number[] {
    const shortDayTranslations = [['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'], ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'], ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'], ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa']];
    return range?.days?.split(',').reduce((res, day) => {
      const arr = shortDayTranslations.find(arr => arr.includes(day));
      if (arr) res.push(arr.indexOf(day));
      return res;
    }, []);
  }

  private removePastItems(array: { start: number, end: number }[]): { start: number, end: number }[] {
    const currentTime = Math.floor(Date.now() / 1000);
    return array?.filter(item => item?.end > currentTime);
  }

  private removePastDates(dates: string[]): string[] {
    const currentDate = new Date();

    return dates?.filter(dateString => {
      const [day, month, year] = dateString.split('.').map(Number);
      const date = new Date(year, month - 1, day); // Convert string to Date object
      return date >= currentDate; // Keep only future or present dates
    });
  }
}
